import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

interface UseCreditsReturn {
  deductCredits: (userId: string, amount: number, action: string) => Promise<boolean>;
  addCredits: (userId: string, amount: number) => Promise<boolean>;
  refreshCredits: (userId: string) => Promise<number>;
  loading: boolean;
}

export const useCredits = (): UseCreditsReturn => {
  const [loading, setLoading] = useState(false);

  const deductCredits = useCallback(async (userId: string, amount: number, action: string): Promise<boolean> => {
    setLoading(true);
    try {
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('credits')
        .eq('id', userId)
        .maybeSingle();

      if (fetchError || !profile) return false;
      if (profile.credits < amount) return false;

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ credits: profile.credits - amount, updated_at: new Date().toISOString() })
        .eq('id', userId);

      if (updateError) return false;

      await supabase.from('usage_logs').insert([{
        user_id: userId,
        action,
        credits_used: amount,
        created_at: new Date().toISOString(),
      }]);

      return true;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const addCredits = useCallback(async (userId: string, amount: number): Promise<boolean> => {
    setLoading(true);
    try {
      const { data: profile, error: fetchError } = await supabase
        .from('profiles')
        .select('credits')
        .eq('id', userId)
        .maybeSingle();

      if (fetchError || !profile) return false;

      const { error } = await supabase
        .from('profiles')
        .update({ credits: profile.credits + amount, updated_at: new Date().toISOString() })
        .eq('id', userId);

      return !error;
    } catch {
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshCredits = useCallback(async (userId: string): Promise<number> => {
    const { data } = await supabase
      .from('profiles')
      .select('credits')
      .eq('id', userId)
      .maybeSingle();
    return data?.credits ?? 0;
  }, []);

  return { deductCredits, addCredits, refreshCredits, loading };
};
